import { CardElement } from "@stripe/react-stripe-js";
import type * as StripeNS from "@stripe/stripe-js";
import type { Stripe, StripeCardElementChangeEvent, StripeElements } from "@stripe/stripe-js";

interface UseSetupIntentArgs {
  stripe: Stripe | null;
  elements: StripeElements | null;
  getClientSecret: () => Promise<string | null>;
  setValue: (
    name: string,
    value: unknown,
    options?: Partial<{ shouldValidate: boolean; shouldDirty: boolean; shouldTouch: boolean }>,
  ) => void;
  setCardError: (message: string | null) => void;
  resetAgreementOnChange?: boolean;
  // Optional callback to provide billing details to Stripe so it can be stored with the PaymentMethod
  getBillingDetails?: () => StripeNS.PaymentMethodCreateParams.BillingDetails;
}

export function useSetupIntent({
  stripe,
  elements,
  getClientSecret,
  setValue,
  setCardError,
  resetAgreementOnChange = true,
  getBillingDetails,
}: UseSetupIntentArgs) {
  const getPaymentMethodId = async (): Promise<boolean> => {
    if (!stripe || !elements) return false;
    try {
      const newClientSecret = await getClientSecret();
      if (!newClientSecret) {
        setCardError("Failed to create payment setup");
        return false;
      }
      setValue("stripeClientSecret", newClientSecret);
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        return false;
      }
      // Log when verifying credit card (and likely followed by tax calc via hooks)
      console.log("verifying credit card and calculating tax");
      const billingDetails =
        typeof getBillingDetails === "function" ? getBillingDetails() : undefined;
      const { error, setupIntent } = await stripe.confirmCardSetup(newClientSecret, {
        payment_method: {
          card: cardElement,
          ...(billingDetails ? { billing_details: billingDetails } : {}),
        },
      });
      if (error) {
        const declineCode = (error as StripeNS.StripeError).decline_code
          ? `Code: ${(error as StripeNS.StripeError).decline_code}.`
          : "";
        const errorCode = error.code ? `Code: ${error.code}.` : "";
        setCardError(
          `${error.message} ${declineCode || errorCode || ""}` || "Card verification failed",
        );
      } else {
        setValue("stripePaymentMethodId", setupIntent.payment_method);
        return true;
      }
    } catch {
      setCardError("Failed to setup payment method");
    }
    return false;
  };

  const handleCreditCardChange = async (event: StripeCardElementChangeEvent) => {
    if (resetAgreementOnChange) {
      setValue("agreement", false, { shouldValidate: true });
    }
    if (event.error) {
      const errorCode = event.error.code ? `Code: ${event.error.code}` : "";
      setCardError(`${event.error.message} ${errorCode}`);
    } else {
      setCardError(null);
    }
    if (!event.complete) {
      return;
    }
    type CardChangeWithPostal = StripeCardElementChangeEvent & {
      value?: { postalCode?: string };
    };
    setValue("billingPostalCode", (event as CardChangeWithPostal)?.value?.postalCode);
    setCardError(null);
    getPaymentMethodId();
  };

  return { getPaymentMethodId, handleCreditCardChange };
}
